<template>
  <div>
    <div v-if="!loading && !isMarketingElementsEmpty" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">
          <erase-icon-button
            v-if="removeButtonVisible"
            class="mr-2"
            @click="onDelete"
          />
          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && isMarketingElementsEmpty"
      :title="$tc('addMarketingCost')"
      :disabled="!canEdit || disabled"
      @click="onShowAddNewDialog"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$tc('addMarketingCost')"
            horizontal
            min-height="62px"
            @click="onShowAddNewDialog"
          />
        </el-col>
        <el-col
          v-for="marketingElement in filterMarketingBudget"
          :key="marketingElement.id"
          class="d-flex mb-2"
          :md="12"
        >
          <set-up-and-projections-card
            class="w-100"
            :entity="marketingElement"
            :name="marketingElement.name"
            :date="marketingElementDate(marketingElement)"
            :amount="marketingElementAmount(marketingElement)"
            :frequency="marketingElement.intervalType === 'one_time' ? $t('pages.businessGuide.oneTime') : $t('pages.businessGuide.monthlyAverage')"
            :type="`${marketingElement.categoryGroup}_marketing_budget`"
            :disabled="!canEdit"
            view="list"
            @edit="onEditMarketingElement"
            @copy="onCopyMarketingElement"
            @delete="onDeleteMarketingElement"
          />
        </el-col>
      </el-row>
    </template>

    <!-- Dialog Add Marketing Budget -->
    <dialog-add-marketing-budget
      v-if="canEdit"
      :marketing-element-action="marketingElementAction"
      :dialog-add-marketing-element-visible="dialogAddNewVisible"
      :on-edit-marketing-element="onEditMarketingElement"
      :on-copy-marketing-element="onCopyMarketingElement"
      :on-delete-marketing-element="onDeleteMarketingElement"
      @close-dialog-add-marketing-element="dialogAddNewVisible = false"
      @close-dialog-on-button="dialogAddNewVisible = false"
      @on-update-marketing-budget-view="onUpdateAddNewDialog()"
    />
    <!-- /Dialog Add Marketing Budget -->
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import DialogAddMarketingBudget from '@/views/Home/StoryMode/Forecast/MarketingBudget/Dialogs/DialogAddMarketingBudget.vue'
import EraseIconButton from '@/components/_v2/EraseIconButton.vue'
import EventBus from '@/event-bus'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDate from '@/mixins/date'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'

export default {
  name: 'MarketingCostsList',

  components: { EraseIconButton, BusinessGuideSearch, DialogAddMarketingBudget, SetUpAndProjectionsCard, AddNewCard },

  mixins: [MixinApiCalculations, MixinCurrencies, MixinDate, MixinDeleteIdeaExamples, MixinIdeaRoles],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchValue: '',
      loading: false,
      marketingElementAction: null,
      dialogAddNewVisible: false
    }
  },

  computed: {
    ...mapState(['idea']),

    ...mapGetters('idea', {
      marketingElements: 'getMarketingElements',
      getStepIsCompleted: 'getStepIsCompleted',
      getHasItemsFromTemplate: 'getHasItemsFromTemplate'
    }),

    isMarketingElementsEmpty () {
      return !this.marketingElements.length
    },

    filterMarketingBudget () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.marketingElements.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.marketingElements
    },

    removeButtonVisible () {
      return this.canEdit && this.marketingCostHasItemsFromTemplate
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onShowAddNewDialog()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })

    EventBus.$on('reload', () => {
      this.onUpdateAddNewDialog()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
    EventBus.$off('reload')
  },

  methods: {
    ...mapActions('idea', [
      'deleteStoryModeMarketingElement'
    ]),

    onShowAddNewDialog () {
      this.getMarketingBudget()
      this.dialogAddNewVisible = true
    },

    onUpdateAddNewDialog () {
      this.emitLoading(true)
      Promise.all([
        this.getMarketingBudget(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    onEditMarketingElement (marketingElement) {
      if (!this.canEdit) return
      this.marketingElementAction = Object.assign({}, marketingElement)
      this.dialogAddNewVisible = true
    },

    onCopyMarketingElement (marketingElement) {
      if (!this.canEdit) return
      this.marketingElementAction = Object.assign({}, marketingElement)
      this.marketingElementAction.id = 0
      this.dialogAddNewVisible = true
    },

    onDeleteMarketingElement (marketingElement) {
      if (!this.canEdit) return
      this.emitLoading(true)
      this.deleteStoryModeMarketingElement(marketingElement.id)
        .then(() => {
          Promise.all([
            this.getMarketingBudget(),
            this.getTotalCosts(),
            this.getProfitAndCashFlow()
          ])
            .then(() => { this.emitLoading(false) })
            .finally(() => { this.emitLoading(false) })
        })
        .catch(() => { this.emitLoading(false) })
    },

    marketingElementDate (marketingElement) {
      if (marketingElement.whenWillItOccur) {
        return this.getFormattedDate(marketingElement.whenWillItOccur)
      }

      return `${this.getFormattedDate(marketingElement.starts)} - ${this.getFormattedDate(marketingElement.ends)}`
    },

    marketingElementAmount (marketingElement) {
      if (marketingElement.average && marketingElement.amountIntervalType !== 'one_time') {
        return this.getFormattedAmount(marketingElement.average)
      }

      return this.getFormattedAmount(marketingElement.amount)
    },

    deleteExamples () {
      this.deleteExamplesMixin('marketing-elements')
        .then(_ => { this.onUpdateAddNewDialog() })
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
